import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  format,
  isSameMonth,
  isSameDay,
  isAfter,
  isBefore,
  eachDayOfInterval,
} from "date-fns";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// API & Data
import { fetchAvailability } from "api";
import { getNightlyRate } from "pages/Booking/sections/data/pricingData";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function isDateBlocked(date, blockedRanges) {
  const dateStr = format(date, "yyyy-MM-dd");
  return blockedRanges.some((range) => dateStr >= range.start && dateStr < range.end);
}

function isDateInRange(date, checkIn, checkOut) {
  if (!checkIn || !checkOut) return false;
  return (isSameDay(date, checkIn) || isAfter(date, checkIn)) && isBefore(date, checkOut);
}

function hasBlockedInRange(start, end, blockedRanges) {
  const days = eachDayOfInterval({ start, end: addDays(end, -1) });
  return days.some((d) => isDateBlocked(d, blockedRanges));
}

function getPriceColor() {
  return "#2e7d32";
}

function MonthCalendar({
  month,
  blockedRanges,
  checkIn,
  checkOut,
  onDateClick,
  today,
  pricingConfig,
  numGuests,
  hidePrices,
}) {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const calStart = startOfWeek(monthStart);
  const calEnd = endOfWeek(monthEnd);

  // Extra guest surcharge per night
  const threshold = pricingConfig?.extraGuestThreshold || 12;
  const feePerPerson = pricingConfig?.extraGuestFee || 0;
  const guests = parseInt(numGuests, 10) || 0;
  const extraPerNight = Math.max(0, guests - threshold) * feePerPerson;

  const rows = [];
  let day = calStart;

  while (day <= calEnd) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const currentDay = day;
      const inMonth = isSameMonth(currentDay, month);
      const isPast = isBefore(currentDay, today) && !isSameDay(currentDay, today);
      const blocked = isDateBlocked(currentDay, blockedRanges);
      const isCheckIn = checkIn && isSameDay(currentDay, checkIn);
      const isCheckOut = checkOut && isSameDay(currentDay, checkOut);
      const inRange = isDateInRange(currentDay, checkIn, checkOut);
      const disabled = !inMonth || isPast || blocked;
      const rate =
        !hidePrices && inMonth && !isPast && !blocked
          ? getNightlyRate(currentDay, pricingConfig) + extraPerNight
          : null;

      let bgColor = "transparent";
      let textColor = "dark";

      if (!inMonth) {
        textColor = "light";
      } else if (isCheckIn || isCheckOut) {
        bgColor = "info";
        textColor = "white";
      } else if (inRange) {
        bgColor = "#e3f2fd";
      } else if (blocked) {
        bgColor = "#e0e0e0";
        textColor = "light";
      } else if (isPast) {
        textColor = "light";
      }

      week.push(
        <MKBox
          key={currentDay.toISOString()}
          onClick={() => !disabled && onDateClick(currentDay)}
          textAlign="center"
          sx={{
            cursor: disabled ? "default" : "pointer",
            borderRadius: "lg",
            width: { xs: 44, sm: 52 },
            height: { xs: 50, sm: 56 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            py: 0.5,
            backgroundColor: bgColor === "info" ? "info.main" : bgColor,
            "&:hover": disabled
              ? {}
              : { backgroundColor: bgColor === "info" ? "info.dark" : "#e0e0e0" },
            transition: "background-color 0.15s",
          }}
        >
          <MKTypography
            variant="button"
            fontWeight={isCheckIn || isCheckOut ? "bold" : "regular"}
            color={textColor}
            sx={
              blocked && inMonth
                ? {
                    textDecoration: "line-through",
                    textDecorationThickness: "2px",
                    ...(textColor === "white" ? { color: "#fff" } : {}),
                    ...(textColor === "light" ? { color: "#ccc" } : {}),
                  }
                : textColor === "white"
                ? { color: "#fff" }
                : textColor === "light"
                ? { color: "#ccc" }
                : {}
            }
          >
            {inMonth ? format(currentDay, "d") : ""}
          </MKTypography>
          {rate && (
            <MKTypography
              sx={{
                fontSize: "0.6rem",
                lineHeight: 1,
                mt: 0.2,
                color:
                  textColor === "white"
                    ? "rgba(255,255,255,0.85)"
                    : getPriceColor(rate, pricingConfig),
                fontWeight: 600,
              }}
            >
              ${rate}
            </MKTypography>
          )}
        </MKBox>
      );
      day = addDays(day, 1);
    }
    rows.push(
      <Grid container key={day.toISOString()} spacing={0}>
        {week.map((cell, i) => (
          <Grid item xs key={i} sx={{ minWidth: 0 }}>
            {cell}
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <MKBox>
      <MKTypography variant="h6" textAlign="center" mb={2}>
        {format(month, "MMMM yyyy")}
      </MKTypography>
      <Grid container spacing={0} mb={1}>
        {WEEKDAYS.map((d) => (
          <Grid item xs key={d} sx={{ minWidth: 0 }}>
            <MKTypography variant="caption" textAlign="center" display="block" fontWeight="bold">
              {d}
            </MKTypography>
          </Grid>
        ))}
      </Grid>
      {rows}
    </MKBox>
  );
}

function AvailabilityCalendar({ onDatesSelected, pricingConfig, numGuests, hidePrices }) {
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [blockedRanges, setBlockedRanges] = useState([]);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const today = new Date();

  useEffect(() => {
    async function loadAvailability() {
      try {
        setLoading(true);
        const start = format(currentMonth, "yyyy-MM-dd");
        const end = format(addMonths(currentMonth, 6), "yyyy-MM-dd");
        const data = await fetchAvailability(start, end);
        setBlockedRanges(data.blockedDates || []);
      } catch (err) {
        console.warn("Could not fetch availability:", err.message);
        setBlockedRanges([]);
      } finally {
        setLoading(false);
        setError(null);
      }
    }
    loadAvailability();
  }, [currentMonth]);

  const handleDateClick = useCallback(
    (date) => {
      if (!checkIn || (checkIn && checkOut)) {
        setCheckIn(date);
        setCheckOut(null);
        onDatesSelected(null, null);
      } else {
        if (isBefore(date, checkIn) || isSameDay(date, checkIn)) {
          setCheckIn(date);
          setCheckOut(null);
          onDatesSelected(null, null);
        } else if (hasBlockedInRange(checkIn, date, blockedRanges)) {
          setCheckIn(date);
          setCheckOut(null);
          onDatesSelected(null, null);
        } else {
          setCheckOut(date);
          onDatesSelected(checkIn, date);
        }
      }
    },
    [checkIn, checkOut, blockedRanges, onDatesSelected]
  );

  const nextMonth = addMonths(currentMonth, 1);

  return (
    <MKBox component="section" py={4}>
      <Container>
        <MKTypography variant="h4" mb={1}>
          Select Your Dates
        </MKTypography>
        <MKTypography variant="body2" color="dark" mb={3}>
          {!checkIn
            ? "Click a date to select check-in"
            : !checkOut
            ? "Now select your check-out date"
            : `${format(checkIn, "MMM d, yyyy")} — ${format(checkOut, "MMM d, yyyy")}`}
        </MKTypography>

        {/* Navigation */}
        <MKBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}>
            <Icon>chevron_left</Icon>
          </IconButton>
          <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
            <Icon>chevron_right</Icon>
          </IconButton>
        </MKBox>

        {loading ? (
          <MKBox py={6} textAlign="center">
            <CircularProgress color="info" size={48} />
            <MKTypography variant="body2" color="dark" mt={2}>
              Loading availability...
            </MKTypography>
          </MKBox>
        ) : error ? (
          <MKTypography variant="body2" color="error" textAlign="center" py={4}>
            {error}
          </MKTypography>
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <MonthCalendar
                month={currentMonth}
                blockedRanges={blockedRanges}
                checkIn={checkIn}
                checkOut={checkOut}
                onDateClick={handleDateClick}
                today={today}
                pricingConfig={pricingConfig}
                numGuests={numGuests}
                hidePrices={hidePrices}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MonthCalendar
                month={nextMonth}
                blockedRanges={blockedRanges}
                checkIn={checkIn}
                checkOut={checkOut}
                onDateClick={handleDateClick}
                today={today}
                pricingConfig={pricingConfig}
                numGuests={numGuests}
                hidePrices={hidePrices}
              />
            </Grid>
          </Grid>
        )}

        {/* Legend */}
        <MKBox display="flex" gap={3} mt={3} flexWrap="wrap" alignItems="center">
          <MKBox display="flex" alignItems="center" gap={1}>
            <MKBox sx={{ width: 16, height: 16, bgcolor: "#fff", border: "1px solid #ccc" }} />
            <MKTypography variant="caption">Available</MKTypography>
          </MKBox>
          <MKBox display="flex" alignItems="center" gap={1}>
            <MKBox sx={{ width: 16, height: 16, bgcolor: "#e0e0e0", border: "1px solid #ccc" }} />
            <MKTypography variant="caption">Booked</MKTypography>
          </MKBox>
          <MKBox display="flex" alignItems="center" gap={1}>
            <MKBox sx={{ width: 16, height: 16, bgcolor: "info.main", borderRadius: "lg" }} />
            <MKTypography variant="caption">Selected</MKTypography>
          </MKBox>
          <MKBox display="flex" alignItems="center" gap={1}>
            <MKBox sx={{ width: 16, height: 16, bgcolor: "#e3f2fd", border: "1px solid #ccc" }} />
            <MKTypography variant="caption">Your stay</MKTypography>
          </MKBox>
        </MKBox>
      </Container>
    </MKBox>
  );
}

MonthCalendar.propTypes = {
  month: PropTypes.instanceOf(Date).isRequired,
  blockedRanges: PropTypes.arrayOf(PropTypes.object).isRequired,
  checkIn: PropTypes.instanceOf(Date),
  checkOut: PropTypes.instanceOf(Date),
  onDateClick: PropTypes.func.isRequired,
  today: PropTypes.instanceOf(Date).isRequired,
  pricingConfig: PropTypes.object,
  numGuests: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hidePrices: PropTypes.bool,
};

AvailabilityCalendar.propTypes = {
  onDatesSelected: PropTypes.func.isRequired,
  pricingConfig: PropTypes.object,
  numGuests: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hidePrices: PropTypes.bool,
};

export default AvailabilityCalendar;
